import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';

const JobCategories = () => {
  const navigate = useNavigate();
  const updatedAt = '2025/09/01';
  const [cms, setCms] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const ENABLE_CMS = process.env.REACT_APP_ENABLE_CMS === '1';

  useEffect(() => {
    if (!ENABLE_CMS) { setLoading(false); return; }
    let aborted = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_BASE}/content/job-categories`);
        if (!r.ok) throw new Error('failed');
        const data = await r.json();
        if (!aborted) setCms({ title: data.title || '', content: data.content || '' });
      } catch (_) {
        if (!aborted) setCms({ title: '', content: '' });
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, []);

  const categories = [
    {
      title: '飲食・フード',
      items: ['ホール・接客', 'キッチン補助', '洗い場', 'デリバリー', '製造補助']
    },
    {
      title: '販売・小売',
      items: ['レジ・接客', '品出し', 'バックヤード', '試食・試飲販売']
    },
    {
      title: 'イベント・キャンペーン',
      items: ['運営サポート', '設営・撤去', '受付', 'サンプリング']
    },
    {
      title: '物流・軽作業',
      items: ['仕分け', 'ピッキング', '梱包', '検品', '搬入・搬出']
    },
    {
      title: 'オフィス・事務補助',
      items: ['データ入力', 'ファイリング', 'スキャン・庶務']
    },
    {
      title: '清掃・メンテナンス',
      items: ['施設清掃', '客室清掃', '売場清掃']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">{ENABLE_CMS && cms.title?.trim() ? cms.title : '取扱職種の範囲'}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center mb-8">
            <BuildingLibraryIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{ENABLE_CMS && cms.title?.trim() ? cms.title : 'バイターズ 取扱職種の範囲'}</h2>
            {!(ENABLE_CMS && cms.content) && (
              <p className="text-gray-600">最終更新日：{updatedAt}</p>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">読み込み中...</div>
          ) : (ENABLE_CMS && cms.content) ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: cms.content }} />
          ) : (
          <div className="space-y-8 text-gray-800 leading-relaxed">
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">対象となるお仕事</h3>
              <p>
                短時間・単発での就業が可能なお仕事を中心に、上記カテゴリの範囲で取り扱っています。安全性・適法性・就業環境を考慮し、当社基準により掲載を審査します。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">主なカテゴリ</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {categories.map((cat) => (
                  <div key={cat.title} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{cat.title}</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      {cat.items.map((it) => (
                        <li key={it}>{it}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">取扱い対象外の例</h3>
              <p className="mb-2">法令・公序良俗に反するもの、危険性が高いもの、専門資格等を要するもの（医療行為等）などは掲載できません。</p>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>違法性のある業務、風営法等で制限される業務</li>
                <li>高度な専門資格・免許が必要な業務（医療・法律実務 等）</li>
                <li>危険物の取り扱い等、特別な安全管理が必要な業務</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">留意事項</h3>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>求人内容は事業者の責任で掲載されます。当社は適切性の確認に努めますが、すべての内容を保証するものではありません。</li>
                <li>就業条件（賃金、時間、場所 等）は個別の求人詳細をご確認ください。</li>
                <li>未成年の方は、保護者等の同意のもとでご利用ください。</li>
              </ul>
            </section>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCategories;